import { Ref } from '@vue/composition-api';
import { STATUS } from '@front-app/constants/state';
import { fetchUsers } from '@front-app/services/UserApi/UserApi';
import { PartialUser, FullUser } from '@front-app/services/UserApi/UserModel';
import { getPartialUserStore } from './user/partialUser';
import { getFullUserStore } from './user/fullUser';
import { STARTABLE_STATUSES } from '../util/cache';

export interface UserStore {
	/**
	 * Add a user to the store
	 * @param user the user to add
	 */
	setPartial(user: PartialUser): void;
	setPartials(user: PartialUser[]): void;
	setFullUser(user: FullUser): void;
	setFullUsers(user: FullUser[]): void;

	/**
	 * Fetch a missing user from the store, or force refetch if force=true
	 * @param id The user to (maybe) fetch
	 * @param force If false (default) skips fetching existing users. If true, will refresh the user's data
	 */
	fetch(id: string, force?: boolean): Promise<void>;
	fetchMany(ids: string[], force?: boolean): Promise<void>;

	/**
	 * Synchronously gets a user from the store if they are there
	 * @param id The user to get from the store
	 */
	getPartial(id: string): Ref<PartialUser>;
	getFullUser(id: string): Ref<FullUser>;
}

export const getUserStore = (): UserStore => {
	const {
		get: getPartial,
		set: setPartialInternal,
	} = getPartialUserStore();
	const {
		get: getFullUser,
		set: setFullUserInternal,
		has: hasFullUser,
	} = getFullUserStore();
	const setFullUser = (newUser: FullUser) => {
		setFullUserInternal(newUser);
		setPartialInternal(newUser);
	};
	const setFullUsers = (newUsers: FullUser[]) => {
		newUsers.forEach(setFullUser);
	};
	const setPartial = (newUser: PartialUser) => {
		if (hasFullUser(newUser.id)) {
			const current = getFullUser(newUser.id).value;
			setFullUser({ ...current, ...newUser });
		} else {
			setPartialInternal(newUser);
		}
	};
	const setPartials = (newUsers: PartialUser[]) => {
		newUsers.forEach(setPartial);
	};
	const fetchMany = async (ids: string[], force = false): Promise<void> => {
		const fetchableUsers = force
			? ids.map(getFullUser).map(({ value }) => value)
			: ids.map(getFullUser).map(({ value }) => value).filter(({ status }) => STARTABLE_STATUSES.includes(status));

		if (fetchableUsers.length === 0) {
			return;
		}

		fetchableUsers.forEach((current) => setFullUser({ ...current, status: STATUS.LOADING }));
		try {
			const users: FullUser[] = await fetchUsers(fetchableUsers.map(({ id }) => id));
			users.forEach((user) => setFullUser({ ...user, status: STATUS.SUCCESS }));
		} catch (e) {
			// eslint-disable-next-line no-console
			console.error('Error fetching/setting full users', e);
			fetchableUsers.forEach((current) => setFullUser({ ...current, status: STATUS.ERROR }));
		}
	};
	const fetch = async (id: string, force = false): Promise<void> => fetchMany([id], force);
	return {
		setPartial,
		setPartials,
		getPartial,
		setFullUser,
		setFullUsers,
		getFullUser,
		fetch,
		fetchMany,
	};
};
